import { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import EditPackageDialog, { type SyncCoaResult } from '../../EditPackageDialog';
import { showToastError, showToastSuccess } from '../../components/ToastNotifications';
import { useLegacyAppSession } from '../../contexts/LegacyAppSessionContext';
import { useSsoReauth } from '../../contexts/SsoReauthContext';
import { editPackage, getPackageById, syncPackageCoa } from '../../hooks/usePackages';

export default function EditPackageContainer() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const { org } = useLegacyAppSession();
  const { handleSsoError } = useSsoReauth();
  const packageRowId = String(params.packageRowId || '');
  const [pkg, setPkg] = useState(location.state?.pkg || null);
  const [isLoading, setIsLoading] = useState(!location.state?.pkg);

  useEffect(() => {
    if (!packageRowId || !org?.id || pkg) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    getPackageById(org.id, packageRowId)
      .then((result) => {
        if (cancelled) return;
        setPkg(result || null);
      })
      .catch(() => {
        if (cancelled) return;
        showToastError('Package could not be loaded.');
      })
      .finally(() => {
        if (cancelled) return;
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [org?.id, packageRowId, pkg]);

  if (!packageRowId) {
    return <Navigate to="/packages" replace />;
  }

  if (isLoading) {
    return null;
  }

  if (!pkg) {
    return <Navigate to="/packages" replace />;
  }

  const handleSyncCoa = async (packageId: number): Promise<SyncCoaResult> => {
    if (!org?.id) {
      throw new Error('Organization is required to relink test results.');
    }

    const organizationId = Number(org.id);
    let result;
    try {
      result = await syncPackageCoa(organizationId, {
        OrganizationId: organizationId,
        PackageId: packageId,
      });
    } catch (error) {
      // The dialog shows its own failure message; the prompt offers the way to fix it.
      if (handleSsoError(error)) {
        throw new Error('Metrc sign-in is required to relink test results.');
      }
      throw error;
    }

    if (result?.error) {
      throw new Error(typeof result?.respBody === 'string' ? result.respBody
        : result?.respBody?.message || result?.respBody?.Message || 'Failed to relink test results. Please try again.');
    }

    const hasWrappedCoa = result != null && typeof result === 'object' && Object.prototype.hasOwnProperty.call(result, 'coa');

    const coa = hasWrappedCoa
      ? (result.coa ?? null)
      : (result ?? null);

    return {
      coaId: result?.coaId ?? result?.id ?? result?.Id ?? null,
      coa,
      quantity: result?.quantity ?? null,
      unitOfMeasureName: result?.unitOfMeasureName ?? null,
      productionBatchNumber: result?.productionBatchNumber ?? null,
    };
  };

  return (
    <EditPackageDialog
      organizationId={org?.id ? String(org.id) : undefined}
      pkg={pkg}
      onClose={() => navigate('/packages', { replace: true })}
      onSyncCoa={handleSyncCoa}
      onSave={async (payload, exit: boolean = true) => {
        if (!org?.id) return;
        try {
          const coaFileEntries = payload?.coaFileEntries || [];
          const coaFiles = coaFileEntries.length > 0
            ? coaFileEntries.map((entry) => ({
              fileId: entry.fileId,
              testPerformedDate: entry.testPerformedDate,
            }))
            : (payload?.coaFileIds || []).map((fileId) => ({
              fileId,
              testPerformedDate: null,
            }));

          await editPackage({
            packageId: payload.id,
            organizationId: org.id,
            packageCoaId: payload.packageCoaId,
            packageCoa: payload.packageCoa,
            manualOverrides: payload.manualOverrides,
            replaceManualOverrides: payload.replaceManualOverrides,
            quantity: payload.quantity,
            facilityId: payload.facilityId,
            productionBatchNumber: payload.productionBatchNumber,
            packageType: payload.packageType,
            packagedDate: payload.packagedDate,
            unitOfMeasureName: payload.unitOfMeasureName,
            coaFiles,
          });
          showToastSuccess('Package successfully saved.');
          if (exit) {
            navigate('/packages', { replace: true });
          }
        } catch (error) {
          showToastError('Package could not be saved.');
          console.error(error);
        }
      }}
    />
  );
}
